import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ArrowLeftRight, Calendar, Clock, User, AlertCircle, CheckCircle, X, Ban, Briefcase, Home, MapPin, Hand } from 'lucide-react';
import { toast } from 'sonner';
import OpenShifts from './OpenShifts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// A pending swap is "expired" once the offered shift has already started (user's local time).
// Times are stored naively (YYYY-MM-DD / HH:MM) and represent the user's local timezone.
const isSwapShiftPast = (swap) => {
  const sched = swap?.schedule;
  if (!sched?.date) return false;
  const [h, m] = (sched.start_time || '00:00').split(':').map(Number);
  const start = new Date(sched.date + 'T00:00:00');
  start.setHours(Number.isFinite(h) ? h : 0, Number.isFinite(m) ? m : 0, 0, 0);
  return start.getTime() <= Date.now();
};

const ShiftSwaps = () => {
  const [swapRequests, setSwapRequests] = useState([]);
  const [pendingApproval, setPendingApproval] = useState([]);
  const [swapHistory, setSwapHistory] = useState([]);
  const [mySchedules, setMySchedules] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [selectedSchedules, setSelectedSchedules] = useState([]); // For multiple shift selection
  const [swapReason, setSwapReason] = useState('');
  const [requiresApproval, setRequiresApproval] = useState(true);
  const [activeTab, setActiveTab] = useState('available'); // 'available', 'pendingApproval', or 'history'
  const [showDenyModal, setShowDenyModal] = useState(false);
  const [selectedSwapForDeny, setSelectedSwapForDeny] = useState(null);
  const [denialReason, setDenialReason] = useState('');
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [selectedSwapForAccept, setSelectedSwapForAccept] = useState(null);
  const [acceptTargetScheduleId, setAcceptTargetScheduleId] = useState('');
  // Open-shift claims awaiting manager approval (consolidated into this tab)
  const [claimedOffers, setClaimedOffers] = useState([]);
  const [showDenyOfferModal, setShowDenyOfferModal] = useState(false);
  const [denyOfferId, setDenyOfferId] = useState(null);
  const [denyOfferReason, setDenyOfferReason] = useState('');

  useEffect(() => {
    fetchSwapRequests();
    fetchPendingApproval();
    fetchClaimedOffers();
    fetchSwapHistory();
    fetchMySchedules();
  }, []);

  const fetchSwapRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/swap-requests?status=pending`, { headers });
      // Hide pending requests whose offered shift has already started — they've expired.
      setSwapRequests((response.data || []).filter(s => !isSwapShiftPast(s)));
    } catch (error) {
      console.error('Failed to fetch swap requests:', error);
      toast.error('Failed to load swap requests');
    }
  };

  const fetchPendingApproval = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const user = JSON.parse(localStorage.getItem('user'));
      // Only managers/admins/team leads can see pending approvals
      if (!['admin', 'manager', 'team_lead'].includes(user.role)) return;

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/swap-requests?status=accepted`, { headers });
      setPendingApproval(response.data || []);
    } catch (error) {
      console.error('Failed to fetch pending approvals:', error);
      toast.error('Failed to load pending approvals');
    }
  };

  const fetchClaimedOffers = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const user = JSON.parse(localStorage.getItem('user'));
      if (!['admin', 'manager', 'team_lead'].includes(user.role)) return;

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/shift-offers?status=all`, { headers });
      setClaimedOffers((response.data || []).filter((o) => o.status === 'claimed'));
    } catch (error) {
      console.error('Failed to fetch claimed offers:', error);
    }
  };

  const handleApproveOffer = async (offerId) => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      await axios.post(`${API}/shift-offers/${offerId}/approve`, {}, { headers });
      toast.success('Approved — the shift now belongs to the claimer');
      fetchClaimedOffers();
      fetchSwapHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    } finally {
      setLoading(false);
    }
  };

  const handleDenyOffer = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const params = denyOfferReason ? `?denial_reason=${encodeURIComponent(denyOfferReason)}` : '';
      await axios.post(`${API}/shift-offers/${denyOfferId}/deny${params}`, {}, { headers });
      toast.success('Claim declined — the shift is open again');
      setShowDenyOfferModal(false);
      setDenyOfferId(null);
      setDenyOfferReason('');
      fetchClaimedOffers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to decline');
    } finally {
      setLoading(false);
    }
  };

  const fetchSwapHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const user = JSON.parse(localStorage.getItem('user'));
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/swap-requests?status=all`, { headers });
      
      // Filter to completed, cancelled, denied, and expired
      let history = (response.data || []).filter(s => 
        s.status === 'completed' || s.status === 'cancelled' || s.status === 'denied' || s.status === 'expired'
      );
      
      // Regular users only see their own swap history
      // Managers/admins/team leads see all history
      if (!['admin', 'manager', 'team_lead'].includes(user.role)) {
        history = history.filter(s => 
          s.requesting_user_id === user.id || s.accepting_user_id === user.id
        );
      }
      
      setSwapHistory(history);
    } catch (error) {
      console.error('Failed to fetch swap history:', error);
    }
  };

  const fetchMySchedules = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const headers = { Authorization: `Bearer ${token}` };
      const user = JSON.parse(localStorage.getItem('user'));
      
      // Fetch ALL of the current user's upcoming shifts (from today onward, no upper cap)
      // so swaps far in the future still have shifts available to offer in return.
      // The server filters by user_id, so this stays lightweight.
      const today = new Date();
      
      const response = await axios.get(
        `${API}/schedules?user_id=${user.id}&date_from=${today.toISOString().split('T')[0]}`,
        { headers }
      );
      
      // Server already scoped to this user; just exclude PTO shifts (not swappable)
      const mySchedules = (response.data || []).filter(s => 
        s.user_id === user.id && 
        s.shift_type !== 'pto'
      );
      
      // Sort by date (earliest first) and then by start time
      // Use .replace(/-/g, '/') to force local time parsing
      mySchedules.sort((a, b) => {
        const dateCompare = new Date(a.date.replace(/-/g, '/')) - new Date(b.date.replace(/-/g, '/'));
        if (dateCompare !== 0) return dateCompare;
        return a.start_time.localeCompare(b.start_time);
      });
      
      setMySchedules(mySchedules);
    } catch (error) {
      console.error('Failed to fetch schedules:', error);
    }
  };

  const handleCreateSwapRequest = async () => {
    // Check if either single or multiple shifts are selected
    const shiftsToSwap = selectedSchedules.length > 0 ? selectedSchedules : (selectedSchedule ? [selectedSchedule] : []);
    
    if (shiftsToSwap.length === 0) {
      toast.error('Please select at least one shift to swap');
      return;
    }

    if (shiftsToSwap.length > 10) {
      toast.error('You can only swap up to 10 shifts at once');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Create a swap request for each selected shift
      // Always require approval for security and scheduling integrity
      const promises = shiftsToSwap.map(scheduleId =>
        axios.post(
          `${API}/swap-requests`,
          {
            schedule_id: scheduleId,
            reason: swapReason,
            requires_approval: true // Always require manager approval
          },
          { headers }
        )
      );

      await Promise.all(promises);

      toast.success(
        shiftsToSwap.length === 1 
          ? 'Swap request created successfully' 
          : `${shiftsToSwap.length} swap requests created successfully`
      );
      setShowCreateModal(false);
      setSelectedSchedule(null);
      setSelectedSchedules([]);
      setSwapReason('');
      fetchSwapRequests();
      fetchPendingApproval();
      fetchSwapHistory();
      fetchMySchedules();
    } catch (error) {
      console.error('Failed to create swap request:', error);
      toast.error(error.response?.data?.detail || 'Failed to create swap request');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptSwap = async (swapId, targetScheduleId) => {
    if (!targetScheduleId) {
      toast.error('Please choose one of your shifts to give in return');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.post(
        `${API}/swap-requests/${swapId}/accept`,
        { target_schedule_id: targetScheduleId },
        { headers }
      );
      
      if (response.data.requires_approval) {
        toast.success('Swap accepted! Waiting for manager approval');
      } else {
        toast.success('Swap completed! Schedules updated');
      }
      
      setShowAcceptModal(false);
      setSelectedSwapForAccept(null);
      setAcceptTargetScheduleId('');
      fetchSwapRequests();
      fetchPendingApproval();
      fetchSwapHistory();
      fetchMySchedules();
    } catch (error) {
      console.error('Failed to accept swap:', error);
      toast.error(error.response?.data?.detail || 'Failed to accept swap');
    } finally {
      setLoading(false);
    }
  };

  const openAcceptModal = (swap) => {
    setSelectedSwapForAccept(swap);
    setAcceptTargetScheduleId('');
    setShowAcceptModal(true);
  };

  const handleApproveSwap = async (swapId) => {
    if (!window.confirm('Approve this shift swap?')) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      await axios.post(`${API}/swap-requests/${swapId}/approve`, {}, { headers });
      toast.success('Swap approved! Schedules updated');
      
      fetchSwapRequests();
      fetchPendingApproval();
      fetchPendingApproval();
      fetchSwapHistory();
    } catch (error) {
      console.error('Failed to approve swap:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve swap');
    } finally {
      setLoading(false);
    }
  };

  const handleDenySwap = (swap) => {
    setSelectedSwapForDeny(swap);
    setDenialReason('');
    setShowDenyModal(true);
  };

  const submitDenySwap = async () => {
    if (!selectedSwapForDeny) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      await axios.post(
        `${API}/swap-requests/${selectedSwapForDeny.id}/deny`,
        null,
        { 
          headers,
          params: { denial_reason: denialReason || undefined }
        }
      );
      
      toast.success('Swap request denied');
      setShowDenyModal(false);
      setSelectedSwapForDeny(null);
      setDenialReason('');
      
      fetchSwapRequests();
      fetchPendingApproval();
      fetchSwapHistory();
    } catch (error) {
      console.error('Failed to deny swap:', error);
      toast.error(error.response?.data?.detail || 'Failed to deny swap');
    } finally {
      setLoading(false);
    }
  };


  const handleDeleteSwap = async (swapId) => {
    if (!window.confirm('Cancel this swap request? This removes it from the pending list and cannot be undone.')) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      await axios.delete(`${API}/swap-requests/${swapId}`, { headers });
      toast.success('Swap request cancelled');
      fetchSwapRequests();
      fetchPendingApproval();
      fetchSwapHistory();
    } catch (error) {
      console.error('Failed to delete swap:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel swap');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelCompletedSwap = async (swapId) => {
    if (!window.confirm('Cancel this completed swap? The schedule will be reverted back to the original user.')) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      await axios.post(`${API}/swap-requests/${swapId}/cancel`, {}, { headers });
      toast.success('Swap cancelled and schedule reverted');
      fetchSwapRequests();
      fetchPendingApproval();
      fetchSwapHistory();
    } catch (error) {
      console.error('Failed to cancel swap:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel swap');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // Use .replace(/-/g, '/') to force local time parsing instead of UTC
    const date = new Date(dateStr.replace(/-/g, '/'));
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isManagerOrAdmin = user.role === 'admin' || user.role === 'manager' || user.role === 'team_lead';

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Shift Exchange</h1>
          <p className="text-gray-600 mt-1">Swap shifts or offer up a shift for a teammate to claim</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-blue-600 hover:bg-blue-700">
          <ArrowLeftRight className="h-4 w-4 mr-2" />
          Request Swap
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex space-x-2 mb-6 border-b">
        <button
          onClick={() => setActiveTab('available')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'available'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Available Swaps
          <Badge className="ml-2 bg-blue-600">{swapRequests.length}</Badge>
        </button>

        <button
          onClick={() => setActiveTab('openShifts')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'openShifts'
              ? 'text-teal-600 border-b-2 border-teal-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          data-testid="tab-open-shifts"
        >
          <span className="inline-flex items-center gap-1.5"><Hand className="h-4 w-4" /> Open Shifts</span>
        </button>
        
        {/* Pending Approval tab only for managers */}
        {isManagerOrAdmin && (
          <button
            onClick={() => setActiveTab('pendingApproval')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'pendingApproval'
                ? 'text-orange-600 border-b-2 border-orange-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Pending Approval
            <Badge className="ml-2 bg-orange-600">{pendingApproval.length + claimedOffers.length}</Badge>
          </button>
        )}
        
        {/* History tab for all users */}
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'history'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {isManagerOrAdmin ? 'History' : 'My History'}
          <Badge className="ml-2 bg-gray-600">{swapHistory.length}</Badge>
        </button>
      </div>

      {/* Available Swaps Tab */}
      {activeTab === 'available' && (
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <ArrowLeftRight className="h-5 w-5 text-blue-600" />
            <span>Available Shifts to Claim</span>
            <Badge className="ml-2 bg-blue-600">{swapRequests.length}</Badge>
          </CardTitle>
          <CardDescription>
            {isManagerOrAdmin 
              ? 'All pending swap requests (you can see all roles)'
              : 'Shifts available for your role - same role required to claim'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {swapRequests.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <ArrowLeftRight className="h-12 w-12 mx-auto mb-3 text-gray-400" />
              <p>No swap requests available</p>
              <p className="text-sm">Check back later or create your own swap request</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {swapRequests.map((swap) => {
                const isMySwap = swap.requesting_user_id === user.id;
                return (
                  <Card key={swap.id} className="border-2">
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center space-x-2">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <User className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{swap.requesting_user?.name}</p>
                              <Badge className="text-xs">{swap.requesting_user?.role}</Badge>
                            </div>
                          </div>
                          {isMySwap && (
                            <Badge className="bg-green-100 text-green-800">Your Request</Badge>
                          )}
                        </div>

                        {/* Shift Type and Location Badges */}
                        <div className="flex flex-wrap gap-2">
                          {/* Shift Type Badge */}
                          <Badge className={
                            swap.schedule?.shift_type === 'on_call' 
                              ? 'bg-purple-100 text-purple-800' 
                              : swap.schedule?.shift_type === 'wfh'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-800'
                          }>
                            {swap.schedule?.shift_type === 'on_call' && '📞 On-Call'}
                            {swap.schedule?.shift_type === 'wfh' && '🏠 WFH'}
                            {swap.schedule?.shift_type === 'regular' && '📅 Regular'}
                            {swap.schedule?.shift_type === 'pto' && '🌴 PTO'}
                          </Badge>
                          
                          {/* Work Location Badge - Only for regular and wfh shifts */}
                          {swap.schedule?.work_location && 
                           swap.schedule?.shift_type !== 'on_call' && 
                           swap.schedule?.shift_type !== 'pto' && (
                            <Badge className={
                              swap.schedule?.work_location === 'remote'
                                ? 'bg-green-100 text-green-800'
                                : swap.schedule?.work_location === 'office'
                                ? 'bg-orange-100 text-orange-800'
                                : 'bg-blue-100 text-blue-800'
                            }>
                              <MapPin className="h-3 w-3 mr-1" />
                              {swap.schedule?.work_location === 'remote' && 'Remote'}
                              {swap.schedule?.work_location === 'office' && 'Office'}
                              {swap.schedule?.work_location === 'hybrid' && 'Hybrid'}
                            </Badge>
                          )}
                        </div>

                        <div className="space-y-2 text-sm">
                          <div className="flex items-center space-x-2 text-gray-700">
                            <Calendar className="h-4 w-4" />
                            <span className="font-medium">{formatDate(swap.schedule?.date)}</span>
                          </div>
                          <div className="flex items-center space-x-2 text-gray-700">
                            <Clock className="h-4 w-4" />
                            <span className="font-medium">{formatTime(swap.schedule?.start_time)} - {formatTime(swap.schedule?.end_time)}</span>
                          </div>
                          {swap.reason && (
                            <div className="flex items-start space-x-2 text-gray-600">
                              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                              <span className="text-xs italic">Reason: {swap.reason}</span>
                            </div>
                          )}
                          {swap.comments && (
                            <div className="flex items-start space-x-2 text-gray-600">
                              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                              <span className="text-xs italic">Comments: {swap.comments}</span>
                            </div>
                          )}
                        </div>

                        {swap.requires_approval && (
                          <Badge className="bg-yellow-100 text-yellow-800 text-xs">
                            ⚠️ Requires Manager Approval
                          </Badge>
                        )}

                        <div className="pt-2 border-t space-y-2">
                          {isMySwap ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteSwap(swap.id)}
                              disabled={loading}
                              className="w-full text-red-600 hover:text-red-700"
                              data-testid={`cancel-swap-btn-${swap.id}`}
                            >
                              <X className="h-4 w-4 mr-2" />
                              Cancel Request
                            </Button>
                          ) : (
                            <>
                              <Button
                                onClick={() => openAcceptModal(swap)}
                                disabled={loading}
                                className="w-full bg-green-600 hover:bg-green-700"
                                data-testid={`accept-swap-btn-${swap.id}`}
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Accept Swap
                              </Button>
                              {isManagerOrAdmin && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDeleteSwap(swap.id)}
                                  disabled={loading}
                                  className="w-full text-red-600 hover:text-red-700 border-red-200"
                                  data-testid={`manager-cancel-swap-btn-${swap.id}`}
                                >
                                  <X className="h-4 w-4 mr-2" />
                                  Cancel Request
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Pending Approval Tab - Managers Only */}
      {activeTab === 'pendingApproval' && isManagerOrAdmin && (
        <>
        {/* Open-shift claims awaiting approval (consolidated here) */}
        {claimedOffers.length > 0 && (
          <Card className="mb-6 border-amber-200" data-testid="claims-awaiting-approval-card">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-amber-700">
                <Hand className="h-5 w-5" />
                <span>Claims Awaiting Your Approval</span>
                <Badge className="ml-2 bg-amber-600">{claimedOffers.length}</Badge>
              </CardTitle>
              <CardDescription>
                A teammate claimed an offered-up shift. Approve to transfer it to the claimer, or decline to reopen it.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {claimedOffers.map((offer) => (
                <div key={offer.id} className="rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid={`claimed-offer-${offer.id}`}>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-700">
                    <span className="inline-flex items-center gap-1.5 font-medium text-gray-900">
                      <Calendar className="h-4 w-4 text-teal-600" />
                      {formatDate(offer.schedule?.date)}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="h-4 w-4 text-gray-400" />
                      {formatTime(offer.schedule?.start_time)} – {formatTime(offer.schedule?.end_time)}
                    </span>
                    {offer.schedule?.role && <Badge variant="outline" className="capitalize">{String(offer.schedule.role).replace(/_/g, ' ')}</Badge>}
                    {offer.schedule?.shift_type === 'on_call' && <Badge className="bg-purple-600">On-Call</Badge>}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm text-gray-700">
                      <span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5" /> {offer.offering_user?.name || 'Someone'}</span>
                      <span className="mx-2 text-gray-400">→</span>
                      claimed by <span className="font-semibold">{offer.claiming_user?.name || 'a teammate'}</span>
                    </p>
                    <div className="flex gap-2">
                      <Button size="sm" className="bg-green-600 hover:bg-green-700" disabled={loading} onClick={() => handleApproveOffer(offer.id)} data-testid={`approve-offer-btn-${offer.id}`}>
                        <CheckCircle className="h-4 w-4 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" disabled={loading} onClick={() => { setDenyOfferId(offer.id); setShowDenyOfferModal(true); }} data-testid={`deny-offer-btn-${offer.id}`}>
                        <X className="h-4 w-4 mr-1" /> Decline
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-orange-600" />
              <span>Awaiting Your Approval</span>
              <Badge className="ml-2 bg-orange-600">{pendingApproval.length}</Badge>
            </CardTitle>
            <CardDescription>
              These swaps have been accepted by both parties and require your approval to finalize
            </CardDescription>
          </CardHeader>
          <CardContent>
            {pendingApproval.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <CheckCircle className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                <p>No swaps pending approval</p>
                <p className="text-sm">All clear! Check back when employees request swaps</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {pendingApproval.map((swap) => (
                  <Card key={swap.id} className="border-2 border-orange-200 bg-orange-50">
                    <CardContent className="p-4">
                      <div className="space-y-4">
                        {/* Requester Info */}
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Requesting</p>
                          <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <User className="h-4 w-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-sm text-gray-900">{swap.requesting_user?.name}</p>
                              <Badge className="text-xs">{swap.requesting_user?.role}</Badge>
                            </div>
                          </div>
                        </div>

                        {/* Swap Arrow */}
                        <div className="flex items-center justify-center">
                          <ArrowLeftRight className="h-5 w-5 text-orange-600" />
                        </div>

                        {/* Accepter Info */}
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Accepting</p>
                          <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                              <User className="h-4 w-4 text-green-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-sm text-gray-900">{swap.accepting_user?.name}</p>
                              <Badge className="text-xs">{swap.accepting_user?.role}</Badge>
                            </div>
                          </div>
                        </div>

                        {/* Shift Details */}
                        <div className="border-t pt-3 space-y-3">
                          {/* Shift Type and Location Badges */}
                          <div className="flex flex-wrap gap-2">
                            {/* Shift Type Badge */}
                            <Badge className={
                              swap.schedule?.shift_type === 'on_call' 
                                ? 'bg-purple-100 text-purple-800' 
                                : swap.schedule?.shift_type === 'wfh'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                            }>
                              {swap.schedule?.shift_type === 'on_call' && '📞 On-Call'}
                              {swap.schedule?.shift_type === 'wfh' && '🏠 WFH'}
                              {swap.schedule?.shift_type === 'regular' && '📅 Regular'}
                              {swap.schedule?.shift_type === 'pto' && '🌴 PTO'}
                            </Badge>
                            
                            {/* Work Location Badge - Only for regular and wfh shifts */}
                            {swap.schedule?.work_location && 
                             swap.schedule?.shift_type !== 'on_call' && 
                             swap.schedule?.shift_type !== 'pto' && (
                              <Badge className={
                                swap.schedule?.work_location === 'remote'
                                  ? 'bg-green-100 text-green-800'
                                  : swap.schedule?.work_location === 'office'
                                  ? 'bg-orange-100 text-orange-800'
                                  : 'bg-blue-100 text-blue-800'
                              }>
                                <MapPin className="h-3 w-3 mr-1" />
                                {swap.schedule?.work_location === 'remote' && 'Remote'}
                                {swap.schedule?.work_location === 'office' && 'Office'}
                                {swap.schedule?.work_location === 'hybrid' && 'Hybrid'}
                              </Badge>
                            )}
                          </div>

                          <div className="space-y-2">
                            <div className="flex items-center space-x-2 text-sm text-gray-700">
                              <Calendar className="h-4 w-4" />
                              <span className="font-medium">{formatDate(swap.schedule?.date)}</span>
                            </div>
                            <div className="flex items-center space-x-2 text-sm text-gray-700">
                              <Clock className="h-4 w-4" />
                              <span className="font-medium">{formatTime(swap.schedule?.start_time)} - {formatTime(swap.schedule?.end_time)}</span>
                            </div>
                            {swap.reason && (
                              <div className="flex items-start space-x-2 text-gray-600">
                                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                <span className="text-xs italic">Reason: {swap.reason}</span>
                              </div>
                            )}
                            {swap.comments && (
                              <div className="flex items-start space-x-2 text-gray-600">
                                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                <span className="text-xs italic">Comments: {swap.comments}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Two-way exchange summary */}
                        <div className="rounded-lg bg-orange-50 border border-orange-200 p-3 space-y-2" data-testid={`swap-exchange-${swap.id}`}>
                          <p className="text-xs font-semibold text-orange-700 uppercase tracking-wide">Shift Exchange</p>
                          <div className="flex items-center gap-2 text-xs text-gray-700">
                            <span className="font-semibold">{swap.accepting_user?.name || 'Accepter'}</span>
                            <span className="text-gray-400">receives</span>
                            <span className="font-medium">{formatDate(swap.schedule?.date)} · {formatTime(swap.schedule?.start_time)}-{formatTime(swap.schedule?.end_time)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-700">
                            <span className="font-semibold">{swap.requesting_user?.name || 'Requester'}</span>
                            <span className="text-gray-400">receives</span>
                            {swap.target_schedule ? (
                              <span className="font-medium">{formatDate(swap.target_schedule?.date)} · {formatTime(swap.target_schedule?.start_time)}-{formatTime(swap.target_schedule?.end_time)}</span>
                            ) : (
                              <span className="italic text-amber-600">no return shift recorded</span>
                            )}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex space-x-2 pt-2 border-t">
                          <Button
                            onClick={() => handleApproveSwap(swap.id)}
                            disabled={loading}
                            className="flex-1 bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Approve
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => handleDenySwap(swap)}
                            disabled={loading}
                            className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="h-4 w-4 mr-2" />
                            Deny
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        </>
      )}

      {/* History Tab - All Users */}
      {activeTab === 'history' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Clock className="h-5 w-5 text-gray-600" />
              <span>{isManagerOrAdmin ? 'Swap History' : 'My Swap History'}</span>
            </CardTitle>
            <CardDescription>
              {isManagerOrAdmin 
                ? 'View all completed, cancelled, and denied swap requests' 
                : 'View your completed, cancelled, and denied swap requests'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {swapHistory.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Clock className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                <p>No swap history yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {swapHistory
                  .sort((a, b) => new Date(b.completed_at || b.created_at) - new Date(a.completed_at || a.created_at))
                  .map((swap) => {
                    const bgColor = swap.status === 'completed' ? 'border-green-200 bg-green-50' : 
                                    swap.status === 'denied' ? 'border-red-200 bg-red-50' : 
                                    swap.status === 'expired' ? 'border-amber-200 bg-amber-50' : 
                                    'border-gray-200 bg-gray-50';
                    const badgeColor = swap.status === 'completed' ? 'bg-green-600' : 
                                       swap.status === 'denied' ? 'bg-red-600' : 
                                       swap.status === 'expired' ? 'bg-amber-600' : 
                                       'bg-gray-600';
                    const statusText = swap.status === 'completed' ? 'Completed' : 
                                       swap.status === 'denied' ? 'Denied' : 
                                       swap.status === 'expired' ? 'Expired' : 
                                       'Cancelled';
                    
                    return (
                  <Card key={swap.id} className={`border ${bgColor}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <Badge className={badgeColor}>
                              {statusText}
                            </Badge>
                            <span className="text-xs text-gray-500">
                              {new Date(swap.completed_at || swap.created_at).toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric', 
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Original User */}
                            <div>
                              <p className="text-xs text-gray-600 mb-1">Original</p>
                              <div className="flex items-center space-x-2">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                  <User className="h-4 w-4 text-blue-600" />
                                </div>
                                <div>
                                  <p className="font-semibold text-sm">{swap.requesting_user?.name}</p>
                                  <Badge className="text-[10px]">{swap.requesting_user?.role}</Badge>
                                </div>
                              </div>
                            </div>

                            {/* Arrow */}
                            <div className="flex items-center justify-center">
                              <ArrowLeftRight className="h-5 w-5 text-gray-400" />
                            </div>

                            {/* Accepting User */}
                            <div>
                              <p className="text-xs text-gray-600 mb-1">
                                {swap.status === 'completed' ? 'Swapped To' : 'N/A'}
                              </p>
                              {swap.accepting_user ? (
                                <div className="flex items-center space-x-2">
                                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                    <User className="h-4 w-4 text-green-600" />
                                  </div>
                                  <div>
                                    <p className="font-semibold text-sm">{swap.accepting_user?.name}</p>
                                    <Badge className="text-[10px]">{swap.accepting_user?.role}</Badge>
                                  </div>
                                </div>
                              ) : (
                                <span className="text-sm text-gray-500">-</span>
                              )}
                            </div>
                          </div>

                          <div className="mt-3 pt-3 border-t grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <div className="flex items-center space-x-2 text-gray-700">
                                <Calendar className="h-3 w-3" />
                                <span className="text-xs">{formatDate(swap.schedule?.date)}</span>
                              </div>
                              <div className="flex items-center space-x-2 text-gray-700 mt-1">
                                <Clock className="h-3 w-3" />
                                <span className="text-xs">
                                  {formatTime(swap.schedule?.start_time)} - {formatTime(swap.schedule?.end_time)}
                                </span>
                              </div>
                            </div>

                            {swap.approver && (
                              <div>
                                <p className="text-xs text-gray-600">Approved by</p>
                                <p className="text-xs font-semibold">{swap.approver?.name}</p>
                              </div>
                            )}
                          </div>

                          {swap.reason && (
                            <div className="mt-2 text-xs text-gray-600 italic">
                              Reason: {swap.reason}
                            </div>
                          )}
                          {swap.comments && (
                            <div className="mt-2 text-xs text-gray-600 italic">
                              Comments: {swap.comments}
                            </div>
                          )}
                          {swap.status === 'denied' && swap.denial_reason && (
                            <div className="mt-2 p-2 bg-red-50 rounded border border-red-200">
                              <p className="text-xs font-semibold text-red-800">Denial Reason:</p>
                              <p className="text-xs text-red-700 italic">{swap.denial_reason}</p>
                            </div>
                          )}
                          
                          {/* Cancel button for completed swaps */}
                          {swap.status === 'completed' && (
                            <div className="mt-3 pt-3 border-t">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleCancelCompletedSwap(swap.id)}
                                disabled={loading}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Ban className="h-4 w-4 mr-1" />
                                Cancel & Revert
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                    );
                  })
                }
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Open Shifts Tab */}
      {activeTab === 'openShifts' && (
        <OpenShifts />
      )}

      {/* Create Swap Request Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request Shift Swap</DialogTitle>
            <DialogDescription>
              Select a shift you want to swap and provide a reason
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Your Shifts to Swap * (Select one or multiple)</Label>
                <span className="text-xs text-gray-500">
                  {selectedSchedules.length} selected {selectedSchedules.length > 0 && `(max 10)`}
                </span>
              </div>
              
              {mySchedules.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm border rounded-lg">
                  No upcoming shifts available
                </div>
              ) : (
                <div className="max-h-64 overflow-y-auto border rounded-lg divide-y">
                  {mySchedules.map((schedule) => {
                    // Determine shift type badge
                    const isOnCall = schedule.shift_type === 'on_call';
                    const isWFH = schedule.shift_type === 'wfh' || schedule.work_location === 'remote';
                    const shiftLabel = isOnCall ? '🔔 On-Call' : 
                                      isWFH ? '🏠 WFH' : 
                                      '📅 Regular';
                    
                    const isSelected = selectedSchedules.includes(schedule.id);
                    
                    return (
                      <div
                        key={schedule.id}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedSchedules(selectedSchedules.filter(id => id !== schedule.id));
                          } else {
                            if (selectedSchedules.length < 10) {
                              setSelectedSchedules([...selectedSchedules, schedule.id]);
                            } else {
                              toast.error('Maximum 10 shifts can be selected at once');
                            }
                          }
                        }}
                        className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                          isSelected ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}} // Controlled by parent div onClick
                            className="mt-1 w-4 h-4 text-blue-600 rounded"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="text-sm font-medium">{shiftLabel}</span>
                              <span className="text-sm text-gray-900 font-semibold">
                                {formatDate(schedule.date)}
                              </span>
                            </div>
                            <div className="text-xs text-gray-600">
                              {formatTime(schedule.start_time)} - {formatTime(schedule.end_time)}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="reason">Reason (Optional)</Label>
              <Textarea
                id="reason"
                value={swapReason}
                onChange={(e) => setSwapReason(e.target.value)}
                placeholder="E.g., Doctor appointment, family event..."
                rows={3}
              />
            </div>

            {/* Manager Approval Requirement - Always enforced for security */}
            <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg">
              <div className="flex items-start space-x-2">
                <input
                  type="checkbox"
                  id="requires-approval"
                  checked={true}
                  disabled={true}
                  className="mt-0.5 w-4 h-4 text-blue-600 rounded opacity-60 cursor-not-allowed"
                />
                <div>
                  <Label htmlFor="requires-approval" className="text-sm font-medium text-yellow-900">
                    Manager approval required
                  </Label>
                  <p className="text-xs text-yellow-700 mt-1">
                    All shift swaps must be approved by a manager or team lead for scheduling integrity.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-xs text-blue-800">
                <strong>Note:</strong> You can select multiple shifts (up to 10) to swap at once. Only team members with the same role can claim your shifts. You can swap regular shifts, on-call shifts, and WFH shifts.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateModal(false);
              setSelectedSchedules([]);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateSwapRequest} 
              disabled={loading || selectedSchedules.length === 0}
            >
              {selectedSchedules.length <= 1 
                ? 'Create Request' 
                : `Create ${selectedSchedules.length} Requests`
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Accept Swap Modal — accepter picks which of their own shifts to give in return */}
      <Dialog open={showAcceptModal} onOpenChange={setShowAcceptModal}>
        <DialogContent className="max-w-md" data-testid="accept-swap-modal">
          <DialogHeader>
            <DialogTitle>Accept Shift Swap</DialogTitle>
            <DialogDescription>
              This is a two-way swap. Pick one of your own shifts to give in return.
            </DialogDescription>
          </DialogHeader>

          {selectedSwapForAccept && (
            <div className="space-y-4 py-2">
              {/* Shift you'll receive */}
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-1">You will receive</p>
                <p className="text-sm font-medium text-gray-900">
                  {selectedSwapForAccept.requesting_user?.name || 'Colleague'}{"'s"} shift
                </p>
                <p className="text-sm text-gray-600 flex items-center gap-2 mt-1">
                  <Calendar className="h-3.5 w-3.5" />
                  {formatDate(selectedSwapForAccept.schedule?.date)}
                  <Clock className="h-3.5 w-3.5 ml-2" />
                  {formatTime(selectedSwapForAccept.schedule?.start_time)} - {formatTime(selectedSwapForAccept.schedule?.end_time)}
                </p>
              </div>

              {/* Your shift to give */}
              <div>
                <Label className="mb-1.5 block">Your shift to give in return</Label>
                <Select value={acceptTargetScheduleId} onValueChange={setAcceptTargetScheduleId}>
                  <SelectTrigger data-testid="accept-target-select">
                    <SelectValue placeholder="Select one of your shifts" />
                  </SelectTrigger>
                  <SelectContent>
                    {mySchedules
                      .filter(s => s.id !== selectedSwapForAccept.schedule_id)
                      .map(s => (
                        <SelectItem key={s.id} value={s.id} data-testid={`accept-target-option-${s.id}`}>
                          {formatDate(s.date)} · {formatTime(s.start_time)} - {formatTime(s.end_time)}
                          {s.shift_type && s.shift_type !== 'regular' ? ` (${s.shift_type})` : ''}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {mySchedules.filter(s => s.id !== selectedSwapForAccept.schedule_id).length === 0 && (
                  <p className="text-xs text-amber-600 mt-1.5">
                    You have no upcoming shifts to offer in return.
                  </p>
                )}
              </div>

              <div className="flex items-start gap-2 p-3 rounded-lg bg-gray-50 border border-gray-200 text-xs text-gray-600">
                <ArrowLeftRight className="h-4 w-4 shrink-0 mt-0.5" />
                <span>You will take their shift and they will take yours.{selectedSwapForAccept.requires_approval ? ' A manager must approve before schedules change.' : ''}</span>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAcceptModal(false)} disabled={loading}>Cancel</Button>
            <Button
              onClick={() => handleAcceptSwap(selectedSwapForAccept?.id, acceptTargetScheduleId)}
              disabled={loading || !acceptTargetScheduleId}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-accept-swap-btn"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Confirm Swap
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deny Swap Modal */}
      <Dialog open={showDenyModal} onOpenChange={setShowDenyModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-red-600">
              <X className="h-5 w-5" />
              <span>Deny Shift Swap</span>
            </DialogTitle>
            <DialogDescription>
              Provide a reason for denying this swap request (optional but recommended)
            </DialogDescription>
          </DialogHeader>

          {selectedSwapForDeny && (
            <div className="space-y-4">
              {/* Swap Details */}
              <div className="p-3 bg-gray-50 rounded-lg border space-y-2">
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-gray-600" />
                  <span className="text-sm font-semibold">{selectedSwapForDeny.requesting_user?.name}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-700">
                  <Calendar className="h-4 w-4" />
                  <span>{formatDate(selectedSwapForDeny.schedule?.date)}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-700">
                  <Clock className="h-4 w-4" />
                  <span>{formatTime(selectedSwapForDeny.schedule?.start_time)} - {formatTime(selectedSwapForDeny.schedule?.end_time)}</span>
                </div>
                {selectedSwapForDeny.reason && (
                  <div className="text-xs text-gray-600 italic mt-2">
                    Request Reason: {selectedSwapForDeny.reason}
                  </div>
                )}
              </div>

              {/* Denial Reason Input */}
              <div>
                <Label htmlFor="denial-reason">Denial Reason (Optional)</Label>
                <Textarea
                  id="denial-reason"
                  value={denialReason}
                  onChange={(e) => setDenialReason(e.target.value)}
                  placeholder="E.g., Insufficient coverage, scheduling conflict, policy violation..."
                  rows={3}
                  className="mt-1"
                />
              </div>

              <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                <p className="text-xs text-red-800">
                  <strong>Warning:</strong> Denying this swap request will cancel it and notify the requesting employee. This action cannot be undone.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDenyModal(false)} disabled={loading}>
              Cancel
            </Button>
            <Button 
              onClick={submitDenySwap} 
              disabled={loading}
              className="bg-red-600 hover:bg-red-700"
            >
              <X className="h-4 w-4 mr-2" />
              Deny Swap
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decline Open-Shift Claim Modal */}
      <Dialog open={showDenyOfferModal} onOpenChange={setShowDenyOfferModal}>
        <DialogContent className="max-w-md" data-testid="deny-offer-modal">
          <DialogHeader>
            <DialogTitle>Decline Claim</DialogTitle>
            <DialogDescription>The shift will reopen so another teammate can claim it.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="mb-1.5 block">Reason (optional)</Label>
            <Textarea value={denyOfferReason} onChange={(e) => setDenyOfferReason(e.target.value)} placeholder="Why are you declining this claim?" data-testid="deny-offer-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDenyOfferModal(false)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" disabled={loading} onClick={handleDenyOffer} data-testid="confirm-deny-offer-btn">
              <X className="h-4 w-4 mr-2" />
              Decline Claim
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShiftSwaps;
